import {
  AllReportsListDocument,
  ReportActionStatus,
  ReportInternalType,
  ReportStatus,
  useAllWorkpacksQuery,
  useCreateBulkReportsMutation,
  useCreateNewReportMutation,
  useGetAllDraftReportsQuery,
  useUpdateReportByPointOfInterestsMutation,
  useValidateReportScopeLazyQuery,
} from '@/__generated__/graphql';
import { useRouter } from '@/hooks';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { ReportCreationMode, ReportGroupDestination } from '@/constants';
import * as state from '@/components/Analysis/state';
import { ReportSubmitState } from '@/types';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import { useGetStructureId } from '../../../../hooks/useGetStructureId';

type Props = {
  selectedPointOfInterestIds?: string[];
  handleClose: () => void;
  internalType: ReportInternalType;
  reportCreated?: () => void;
  componentIds?: string[];
  openComponentReportDialog?: () => void;
};

type SubmittingReportState = 'Loading' | 'Success' | undefined;

const reportNameRegex = /^[\w\s(){}[\]?/&+=.\\-]{5,200}$/;

export const useReportFormLogic = ({
  selectedPointOfInterestIds = [],
  handleClose,
  internalType,
  reportCreated,
  componentIds = [],
  openComponentReportDialog = () => {},
}: Props) => {
  const router = useRouter();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const structureId = useGetStructureId();

  const [reportState, setReportState] = useState<ReportSubmitState>('create');
  const [creationMode, setCreationMode] = useState(ReportCreationMode.Single);
  const [reportName, setReportName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [groupReportName, setGroupReportName] = useState('');
  const [workpackNumber, setWorkpackNumber] = useState('');
  const [destination, setDestination] = useState(ReportGroupDestination.GeneralReports);
  const [actionStatus, setActionStatus] = useState<ReportActionStatus>(ReportActionStatus.Backlog);
  const [dueDate, setDueDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [reportId, setReportId] = useState('');
  const [submittingReportState, setSubmittingReportState] = useState<SubmittingReportState>();
  const [openInvalidScopeDialog, setOpenInvalidScopeDialog] = useState(false);
  const appliedAssemblyFilters = useAtomValue(state.appliedAssemblyFilters);
  const selectedAssemblyIds = useAtomValue(state.selectedAssemblyIds);
  const assembliesCount = useAtomValue(state.assembliesCount);
  const isAllComponentsSelected = useAtomValue(state.isAllComponentsSelected);

  const resetStates = useCallback(() => {
    // Only reset local useState variables
    setReportState('create');
    setCreationMode(ReportCreationMode.Single);
    setReportName('');
    setGroupName('');
    setGroupReportName('');
    setWorkpackNumber('');
    setDestination(ReportGroupDestination.GeneralReports);
    setActionStatus(ReportActionStatus.Backlog);
    setDueDate('');
    setStartDate('');
    setIsFocused(false);
    setReportId('');
    setSubmittingReportState(undefined);
    setOpenInvalidScopeDialog(false);
    // Note: We're not resetting any Recoil state variables
  }, []);

  const [createNewReportMutation] = useCreateNewReportMutation();
  const [createBulkReportsMutation] = useCreateBulkReportsMutation();
  const [updateReportByPointOfInterestsMutation] = useUpdateReportByPointOfInterestsMutation();
  const getAllDraftReportsQueryResult = useGetAllDraftReportsQuery({
    variables: {
      input: {
        structureId,
        status: ReportStatus.Draft,
        internalType: internalType,
      },
    },
    skip: internalType === ReportInternalType.Component,
  });

  const { data: allWorkpacks } = useAllWorkpacksQuery({
    variables: {
      structureId,
    },
  });

  const allWorkpackNames = useMemo(() => {
    return allWorkpacks?.allWorkpacks.map((workpack) => workpack.name) ?? [];
  }, [allWorkpacks?.allWorkpacks]);

  const getReportNameError = (name: string): string | undefined => {
    if (name.length < 5) return 'Report name must be at least 5 characters.';
    if (name.length > 200) return 'Report name must be no more than 200 characters.';
    if (!reportNameRegex.test(name)) return 'Report name contains invalid characters.';
    return undefined;
  };

  const handleRadioChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (['create', 'edit'].includes(value)) setReportState(value as ReportSubmitState);
  }, []);

  const handleReportNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setReportName(event.target.value);
  }, []);

  const handleGroupNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setGroupName(event.target.value);
  }, []);

  const handleGroupReportNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setGroupReportName(event.target.value);
  }, []);

  const handleWorkpackNumberChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setWorkpackNumber(event.target.value);
  }, []);

  const handleReportDueDateChange = useCallback((value: unknown) => {
    if (!value) return;
    setDueDate(value.toString());
  }, []);

  const handleReportStartDateChange = useCallback((value: unknown) => {
    if (!value) return;
    setStartDate(value.toString());
  }, []);

  const handleReportActionStatusChange = useCallback((value: string | null) => {
    if (!value) return;
    setActionStatus(
      Object.values(ReportActionStatus).find((status) => status === value) ??
        ReportActionStatus.Backlog
    );
  }, []);

  const handleWorkpackSelection = useCallback((value: string) => {
    setWorkpackNumber(value ?? '');
  }, []);

  const handleDestinationChange = useCallback((value: string) => {
    setDestination(value);
  }, []);

  const handleReportIdChanged = useCallback(
    (_: unknown, report: { label: string; id: string } | null) => {
      if (!report) return;
      setReportId(report.id);
    },
    []
  );

  const allComponentIds = useMemo(() => {
    // If specific component IDs are provided, use them
    if (componentIds?.length > 0) {
      return componentIds;
    }

    // If all components are selected, return empty array
    // (This indicates that all components should be included)
    if (isAllComponentsSelected) {
      return [];
    }

    // Otherwise, use the selected assembly IDs or empty array as fallback
    return selectedAssemblyIds ?? [];
  }, [selectedAssemblyIds, componentIds, isAllComponentsSelected]);

  const totalComponentsCount = useMemo(() => {
    // If we have specific component IDs, use their count
    if (allComponentIds?.length > 0) {
      return allComponentIds.length;
    }

    // If all components are selected, use the total assemblies count
    if (isAllComponentsSelected) {
      return assembliesCount ?? 0;
    }

    // Otherwise, use the selected assembly IDs count or 0 as fallback
    return assembliesCount ?? 0;
  }, [allComponentIds, assembliesCount, isAllComponentsSelected]);

  const [validateReportScope] = useValidateReportScopeLazyQuery({
    variables: {
      input: {
        structureId,
        componentIds: allComponentIds,
        selectedDeckIds: appliedAssemblyFilters.deckFilters,
        selectedPaintRegionIds: appliedAssemblyFilters.paintRegionFilters,
        selectedAssemblyTemplateFilters: appliedAssemblyFilters.templateFilters,
        selectedPoiTypes: appliedAssemblyFilters.poiTypeFilters,
      } as any,
    },
  });

  const filterOptions = createFilterOptions({
    limit: 5,
    matchFrom: 'start',
    ignoreCase: true,
  });

  const numberOfReports = useMemo(() => {
    return creationMode === ReportCreationMode.Single ? 1 : (totalComponentsCount ?? 0);
  }, [creationMode, totalComponentsCount]);

  const handleCreationModeChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if ([ReportCreationMode.Single, ReportCreationMode.Seperate].includes(value))
      setCreationMode(value);
    setDestination(
      value === ReportCreationMode.Single
        ? ReportGroupDestination.GeneralReports
        : ReportGroupDestination.NewGroup
    );
  }, []);

  const filteringNotApplied = useMemo(() => {
    const noFiltersApplied =
      appliedAssemblyFilters.deckFilters.length === 0 &&
      appliedAssemblyFilters.paintRegionFilters.length === 0 &&
      appliedAssemblyFilters.poiTypeFilters.length === 0 &&
      appliedAssemblyFilters.templateFilters.length === 0 &&
      selectedAssemblyIds.length === 0 &&
      componentIds.length === 0 &&
      !isAllComponentsSelected;

    const allComponentsSelectedWithoutFilters =
      isAllComponentsSelected &&
      appliedAssemblyFilters.deckFilters.length === 0 &&
      appliedAssemblyFilters.paintRegionFilters.length === 0 &&
      appliedAssemblyFilters.poiTypeFilters.length === 0 &&
      appliedAssemblyFilters.templateFilters.length === 0;

    return noFiltersApplied || allComponentsSelectedWithoutFilters;
  }, [appliedAssemblyFilters, selectedAssemblyIds, isAllComponentsSelected, componentIds]);

  const handleCreateReport = useCallback(
    async (bypassScope?: boolean) => {
      const commonInput = {
        internalType,
        name: reportName,
        structureId,
        actionStatus,
        ...(dueDate ? { dueDate } : {}),
        ...(startDate ? { startDate } : {}),
        ...(workpackNumber ? { workpackNumber } : {}),
      };
      if (internalType === ReportInternalType.Component && !bypassScope) {
        const { data: validateReportScopeCheck } = await validateReportScope();
        if (!validateReportScopeCheck?.validateReportScope?.isValid) {
          handleClose();
          setSubmittingReportState(undefined);
          setOpenInvalidScopeDialog(true);
          return;
        }
      }
      if (
        internalType === ReportInternalType.Component &&
        creationMode === ReportCreationMode.Seperate
      ) {
        try {
          await createBulkReportsMutation({
            variables: {
              input: {
                ...commonInput,
                groupName,
                name: groupReportName,
                ...(allComponentIds?.length > 0 && {
                  componentIds: allComponentIds,
                }),
                ...(allComponentIds?.length === 0 && {
                  selectedDeckIds: appliedAssemblyFilters.deckFilters,
                  selectedPaintRegionIds: appliedAssemblyFilters.paintRegionFilters,
                  selectedAssemblyTemplateFilters: appliedAssemblyFilters.templateFilters,
                  selectedPoiTypes: appliedAssemblyFilters.poiTypeFilters,
                }),
              },
            },
            refetchQueries: [AllReportsListDocument],
          });
          setSubmittingReportState('Success');
          showSnackBar('Reports have been created successfully', 'success');
          setTimeout(() => {
            handleClose();
            const url = `/analysis/${structureId}/reports/`;
            internalType === ReportInternalType.Component
              ? window.open(url, '_blank')
              : router.push(url);
          }, 1000);
          resetStates();
        } catch {
          showSnackBar(`Error creating reports`, 'error');
        }
      } else {
        try {
          const newReport = await createNewReportMutation({
            variables: {
              input: {
                ...commonInput,
                ...(internalType === ReportInternalType.Component &&
                  allComponentIds?.length > 0 && {
                    componentIds: allComponentIds,
                  }),
                ...(internalType === ReportInternalType.Component &&
                  allComponentIds?.length === 0 && {
                    selectedDeckIds: appliedAssemblyFilters.deckFilters,
                    selectedPaintRegionIds: appliedAssemblyFilters.paintRegionFilters,
                    selectedAssemblyTemplateFilters: appliedAssemblyFilters.templateFilters,
                    selectedPoiTypes: appliedAssemblyFilters.poiTypeFilters,
                  }),
              },
            },
            refetchQueries: [AllReportsListDocument],
          });
          const newReportId = newReport?.data?.createNewReport?.id;
          setSubmittingReportState('Success');
          showSnackBar('Report has been created successfully', 'success');
          setTimeout(() => {
            handleClose();
            const url = `/analysis/${structureId}/reports/${newReportId ?? ''}`;
            internalType === ReportInternalType.Component
              ? window.open(url, '_blank')
              : router.push(url);
          }, 1000);
          resetStates();
        } catch {
          showSnackBar(`Error creating report ${reportName}`, 'error');
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      internalType,
      reportName,
      structureId,
      actionStatus,
      dueDate,
      workpackNumber,
      validateReportScope,
      creationMode,
      handleClose,
      createBulkReportsMutation,
      groupName,
      groupReportName,
      allComponentIds,
      appliedAssemblyFilters.deckFilters,
      appliedAssemblyFilters.paintRegionFilters,
      appliedAssemblyFilters.templateFilters,
      appliedAssemblyFilters.poiTypeFilters,
      showSnackBar,
      resetStates,
      router,
      createNewReportMutation,
    ]
  );

  const handleAddPOIToReport = useCallback(async () => {
    try {
      await updateReportByPointOfInterestsMutation({
        variables: {
          input: {
            reportId,
            pointOfInterestIds: selectedPointOfInterestIds,
          },
        },
        refetchQueries: [AllReportsListDocument],
      });
      setSubmittingReportState('Success');
      setTimeout(() => {
        handleClose();
        reportCreated?.();
        resetStates();
      }, 3000);
    } catch {
      showSnackBar('Error while updating report', 'error');
    }
  }, [
    handleClose,
    reportCreated,
    reportId,
    resetStates,
    selectedPointOfInterestIds,
    showSnackBar,
    updateReportByPointOfInterestsMutation,
  ]);

  const handleAddOrCreateReport = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>, bypassScope?: boolean) => {
      event?.preventDefault();
      setSubmittingReportState('Loading');
      if (reportState === 'create') {
        handleCreateReport(bypassScope);
      } else {
        handleAddPOIToReport();
      }
    },
    [handleCreateReport, handleAddPOIToReport, reportState]
  );

  const draftReports = useMemo(() => {
    return getAllDraftReportsQueryResult.data?.allReports ?? [];
  }, [getAllDraftReportsQueryResult.data?.allReports]);

  const mappedDraftReports = useMemo(() => {
    return draftReports.map((report) => ({
      label: report?.name ?? '',
      id: report?.id ?? '',
    }));
  }, [draftReports]);

  const isInValidReportEntry = useMemo(() => {
    if (reportState === 'edit' && !reportId) return true;
    if (
      reportState === 'create' &&
      creationMode === ReportCreationMode.Single &&
      !reportNameRegex.test(reportName)
    )
      return true;
    if (
      reportState === 'create' &&
      creationMode === ReportCreationMode.Seperate &&
      !reportNameRegex.test(groupReportName)
    )
      return true;
    return false;
  }, [creationMode, groupReportName, reportId, reportName, reportState]);

  const handleByPassInvalidScopeDialog = useCallback(() => {
    handleAddOrCreateReport(undefined, true);
  }, [handleAddOrCreateReport]);

  const handleCancelInvalidScopeDialog = useCallback(() => {
    setOpenInvalidScopeDialog(false);
    openComponentReportDialog();
  }, [openComponentReportDialog]);

  const handleCloseAndResetStates = useCallback(() => {
    handleClose();
    resetStates();
  }, [handleClose, resetStates]);

  return {
    creationMode,
    filterOptions,
    numberOfReports,
    destination,
    allWorkpackNames,
    reportState,
    reportName,
    workpackNumber,
    actionStatus,
    dueDate,
    startDate,
    isFocused,
    reportId,
    submittingReportState,
    draftReports,
    mappedDraftReports,
    isInValidReportEntry,
    totalComponentsCount,
    filteringNotApplied,
    groupReportName,
    openInvalidScopeDialog,
    setOpenInvalidScopeDialog,
    handleCreationModeChanged,
    getReportNameError,
    handleRadioChanged,
    handleReportNameChange,
    handleWorkpackNumberChange,
    handleReportDueDateChange,
    handleReportStartDateChange,
    handleReportActionStatusChange,
    handleReportIdChanged,
    handleAddOrCreateReport,
    handleWorkpackSelection,
    handleDestinationChange,
    handleGroupNameChange,
    handleGroupReportNameChange,
    setIsFocused,
    handleByPassInvalidScopeDialog,
    handleCancelInvalidScopeDialog,
    resetStates,
    handleCloseAndResetStates,
  };
};
